// src/components/Navbar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Navbar, Container, Nav, Button, NavDropdown, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { normalizeRole, isDonorRole, isRequesterRole, isSuperadminRole } from "../utils/roles";

const NavbarComponent = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const navigate = useNavigate();

  // Traer rol del usuario autenticado
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.uid) {
        setRole(null);
        return;
      }
      try {
        setLoadingRole(true);
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setRole(normalizeRole(userDoc.data().role));
        } else {
          // si no hay doc, por defecto lo tratamos como solicitante
          setRole("requester");
        }
      } catch (e) {
        console.error("Error obteniendo rol:", e);
        setRole(null);
      } finally {
        setLoadingRole(false);
      }
    };
    fetchUserRole();
  }, [user?.uid]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión: ", error);
    }
  };

  const isDonor = isDonorRole(role);
  const isRequester = isRequesterRole(role);
  const isSuperadmin = isSuperadminRole(role);

  // Mostrar un mini estado de carga cuando aún no resolvimos auth/rol
  const isBusy = useMemo(() => loadingAuth || loadingRole, [loadingAuth, loadingRole]);

  return (
    <Navbar bg="light" expand="lg" className="shadow-sm">
      <Container>
        <Navbar.Brand as={Link} to="/" className="fw-bold">
          - Farmacia Solidaria -
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-navbar" />

        <Navbar.Collapse id="main-navbar">
          <Nav className="me-auto">
            <Nav.Link as={Link} to="/">Inicio</Nav.Link>
            <Nav.Link as={Link} to="/about">Acerca de</Nav.Link>
            <Nav.Link as={Link} to="/noticias">Noticias</Nav.Link>

            {/* Menú de medicamentos solo si hay sesión */}
            {user && (
              <NavDropdown title="Medicamentos" id="medications-dropdown">
                {/* DONANTE */}
                {isDonor && (
                  <>
                    <NavDropdown.Item as={Link} to="/add-medication">
                      Agregar Medicamento
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/medications">
                      Mis Medicamentos
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/received-requests">
                      Solicitudes Recibidas
                    </NavDropdown.Item>
                  </>
                )}

                {/* SOLICITANTE */}
                {isRequester && (
                  <>
                    <NavDropdown.Item as={Link} to="/request-medication">
                      Solicitar Medicamento
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/my-requests">
                      Mis Solicitudes
                    </NavDropdown.Item>
                  </>
                )}

                {/* SUPERADMIN (opcional: acceso rápido a solicitudes globales) */}
                {isSuperadmin && (
                  <>
                    <NavDropdown.Divider />
                    <NavDropdown.Item as={Link} to="/received-requests">
                      Panel de Solicitudes
                    </NavDropdown.Item>
                  </>
                )}
              </NavDropdown>
            )}

            {/* Link directo a Admin Noticias solo visible a superadmin */}
            {user && isSuperadmin && (
              <Nav.Link as={Link} to="/admin/noticias">
                Admin Noticias
              </Nav.Link>
            )}
          </Nav>

          <Nav>
            {isBusy ? (
              <div className="d-flex align-items-center px-2">
                <Spinner animation="border" size="sm" className="me-2" /> Cargando...
              </div>
            ) : user ? (
              <>
                <Navbar.Text className="me-3">
                  Bienvenido, <strong>{user.email}</strong> {role ? `(${role})` : ""}
                </Navbar.Text>
                <Button variant="outline-danger" onClick={handleLogout}>
                  Cerrar sesión
                </Button>
              </>
            ) : (
              <Nav.Link as={Link} to="/login">
                Iniciar sesión
              </Nav.Link>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavbarComponent;
